import React from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { Field, reduxForm, stopSubmit } from "redux-form"
import {
  compose,
  withProps,
  withPropsOnChange,
  withStateHandlers,
} from "recompact"
import queryString from "query-string"
import { Box } from "@infotech/uikit-grid"
import noop from "lodash/noop"
import map from "ramda/src/map"
import Select from "@infotech/uikit-select"
import FieldSet from "@infotech/uikit-fieldset"
import { Caption, P } from "@infotech/uikit-text"
import Input, { InputNumber } from "@infotech/uikit-input"
import { fieldWrapper } from "src/engine/modules/reduxForm"
import { PrimaryButton, SecondaryButton } from "@infotech/uikit-button"
import ModalConfirm from "src/components/modals/ModalConfirm"
import withQueryParams from "src/hocs/withQueryParams"
import workspace from "src/hocs/workspace"
import { PLAN_TYPES_URL } from "src/constants/routeNames"
import get from "lodash/get"
import withSideModalPending from "src/hocs/withSideModalPending"
import makeCompareFunction from "src/helpers/makeCompareFunction"
import LinkContent from "src/components/LinkContent"
import EventTypePart from "./EventType"
import TemplatesPart from "./Templates"
import styles from "./styles.css"

export const FORM_NAME = `createPlanType`
const getTestAttribute = name => `planner-plan-type-item-${name}`

const InputRF = fieldWrapper(Input)
const InputNumberRF = fieldWrapper(InputNumber)
const SelectRF = fieldWrapper(Select)

const durationUnitOptions = [
  { code: `DAY`, name: `дн.` },
  { code: `WEEK`, name: `нед.` },
  { code: `MONTH`, name: `мес.` },
  { code: `YEAR`, name: `г.` },
]

const NumberField = ({ name, caption }) => (
  <Box flex loose align="center" mr="1md">
    <Box loose w="9md" shrink={0} mr="1ui">
      <Field name={name} component={InputNumber} />
    </Box>
    <Caption style={{ color: `#b2b2b2` }}>{caption}</Caption>
  </Box>
)

NumberField.propTypes = {
  name: PropTypes.string.isRequired,
  caption: PropTypes.string.isRequired,
}

const Item = props => {
  const {
    dirty,
    handleSubmit,
    change,
    history,
    id,
    queryParams,
    isConfirmationModalOpen,
    openConfirmationModal,
    closeConfirmationModal,
    initialValues,
    planType,
    disableEdit,
  } = props

  const isNew = Boolean(id === `new`)
  const isDisabledByPermission = disableEdit && !isNew
  const haveLink = Boolean(get(planType, `data.externalLinkTemplate`))
  const closeModal = () => {
    history.push(`${PLAN_TYPES_URL}/?${queryString.stringify(queryParams)}`)
  }

  const submitButton = isDisabledByPermission ? null : (
    <Box loose mr="2md">
      <PrimaryButton
        className={styles.Buttons}
        onClick={handleSubmit}
        disabled={!dirty && Boolean(initialValues)}
      >
        {isNew ? `Создать` : `Сохранить`}
      </PrimaryButton>
    </Box>
  )

  return (
    <Box flex style={{ backgroundColor: `#fff` }}>
      <Box flex column className={styles.mainContentColumn}>
        <Box loose flex column p="2md" className={styles.ContentWrapper}>
          <FieldSet caption="Наименование типа плана" required>
            <Field
              name="name"
              placeholder="Введите наименование типа плана"
              component={InputRF}
              getProps={meta => ({
                error: meta.error,
              })}
              props={{
                disabled: isDisabledByPermission,
              }}
            />
          </FieldSet>
          <Box loose pt="2md">
            <FieldSet caption="Длительность типа плана" required>
              <Box loose flex>
                <Box loose w="70px">
                  <div data-testid={getTestAttribute(`plan-duration-count`)}>
                    <Field
                      name="defaultPlanDurationValue"
                      component={InputNumberRF}
                      getProps={meta => ({
                        error: meta.error,
                      })}
                      props={{
                        disabled: isDisabledByPermission,
                      }}
                    />
                  </div>
                </Box>
                <Box loose w="116px" pl="3md">
                  <div data-testid={getTestAttribute(`plan-duration-unit`)}>
                    <Field
                      name="defaultPlanDurationUnit"
                      component={SelectRF}
                      props={{
                        options: durationUnitOptions.map(({ name, code }) => ({
                          value: code,
                          label: name,
                        })),
                        disabled: isDisabledByPermission,
                      }}
                      getProps={(meta, input) => ({
                        onBlur: () => input.onBlur(),
                        onChange: ({
                          target: {
                            value: { value },
                          },
                        }) => input.onChange(value),
                        error: meta.error,
                      })}
                    />
                  </div>
                </Box>
              </Box>
            </FieldSet>
          </Box>
          <Box loose pt="2md">
            <FieldSet caption="Код" required>
              <Field
                name="code"
                placeholder="Введите код"
                component={InputRF}
                getProps={meta => ({
                  error: meta.error,
                })}
                props={{
                  disabled: isDisabledByPermission,
                }}
              />
            </FieldSet>
          </Box>
          <LinkContent
            haveLink={haveLink}
            isDisabled={isDisabledByPermission}
            changeFormValue={change}
            getTestAttribute={getTestAttribute}
          />
        </Box>
        <Box loose flex className={styles.buttonWrapper} p="2md">
          {submitButton}
          <Box loose onClick={dirty ? openConfirmationModal : closeModal}>
            <SecondaryButton className={styles.Buttons}>Отмена</SecondaryButton>
          </Box>
        </Box>
      </Box>
      <Box className={styles.mainContentColumn}>
        <EventTypePart
          planType={planType}
          isDisabled={isDisabledByPermission}
        />
      </Box>
      <Box className={styles.mainContentColumn}>
        <TemplatesPart
          planType={planType}
          isDisabled={isDisabledByPermission}
        />
      </Box>
      <ModalConfirm
        isOpen={isConfirmationModalOpen}
        title="Закрыть без сохранения?"
        buttonConf={[
          {
            text: `Закрыть`,
            onClick: dirty ? closeModal : noop,
            isOk: true,
            done: true,
            disabled: false,
          },
          {
            text: `Отмена`,
            onClick: closeConfirmationModal,
            disabled: false,
          },
        ]}
        isClose
      >
        <div className={styles[`modal-message-wrapper`]}>
          <P className={styles[`modal-message-text`]}>
            Все сделанные вами изменения будут утеряны
          </P>
        </div>
      </ModalConfirm>
    </Box>
  )
}

Item.propTypes = {
  params: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  dirty: PropTypes.bool.isRequired,
  history: PropTypes.shape({
    location: PropTypes.object,
  }).isRequired,
  queryParams: PropTypes.oneOfType([PropTypes.object, PropTypes.number]),
  isConfirmationModalOpen: PropTypes.bool.isRequired,
  openConfirmationModal: PropTypes.func.isRequired,
  closeConfirmationModal: PropTypes.func.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  initialValues: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  disableEdit: PropTypes.bool.isRequired,
}

Item.defaultProps = {
  queryParams: {},
}

const enhancer = compose(
  withQueryParams(),
  withStateHandlers(
    () => ({
      isConfirmationModalOpen: false,
    }),
    {
      openConfirmationModal: () => () => ({
        isConfirmationModalOpen: true,
      }),
      closeConfirmationModal: () => () => ({
        isConfirmationModalOpen: false,
      }),
    },
  ),
  withProps(({ dataForTabs }) => ({
    initialValues: get(dataForTabs, `planType.data`),
    planType: dataForTabs.planType,
  })),
  workspace,
  connect(null, {
    setErrors: errors => stopSubmit(FORM_NAME, errors),
  }),
  withPropsOnChange(
    makeCompareFunction([`planType.error`]),
    ({ planType: { error }, setErrors }) => {
      if (
        error &&
        error.data &&
        error.data.errorCode === `application.request.validation.failed`
      ) {
        const { exception, body } = error.data
        const errorsObject = map(array => array[0])(body) || {}
        setErrors({
          ...map(array => array[0])(body),
          externalLinkTemplate: {
            name: errorsObject[`externalLinkTemplate.name`],
            template: errorsObject[`externalLinkTemplate.template`],
          },
          _error: exception,
        })
      }
    },
  ),
  reduxForm({
    form: FORM_NAME,
    enableReinitialize: true,
    onSubmit(
      data,
      dispatch,
      {
        planType: {
          api: { save },
        },
        workspace: { emit },
      },
    ) {
      emit(`save`)
      save(data)
    },
  }),
  withSideModalPending(`planType`, `Тип плана не найден`, true),
)

export default enhancer(Item)
